import React from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';
import ErrorView from './ErrorView';
import { useTypedDispatch, useTypedSelector } from '@profile/src/redux/utils';
import icArrowDown from 'assets/svg/arrow_down_V2.svg';
import icQuestionMark from 'assets/svg/ic_question_mark_V2.svg';
import icRefresh from 'assets/svg/ic_refresh_V2.svg';
import { checkAccountCashout, claimToken, confirmCashout, fetchCashOutLimit, fetchIngameBalance, prepareClaimToken } from '@profile/src/redux/actions';
import Modal from '@profile/src/Components/PopupModal_V2';
import { AppError } from '@profile/src/redux/types';
import { coinTypeMap, formatErrorMessage, getSystemCurrency, checkOverPriceError, getNotOldErrorMessage } from './utils';
import { InGameCurrency } from '../../config';
import { CurrencyType, formatCurrency, normalNumberToSystemCurrency } from '@wolffun/system-currency';
import { logCoinChangeMarket } from '@profile/src/utils/analytic';
import { ellipsisEnd } from '@profile/src/utils/text';
import BN from 'bn.js';
import Tooltip from './Components/Tooltip';
import { TooltipPosition } from '@profile/src/Components/Tooltip/types';
import BreakpointContent from '@profile/src/Components/BreakpointContent';
import Button from '@profile/src/Components/Button_V2';
import { useConfirmOpenWalletConnect } from '@profile/src/Components/WalletConnectConfirmProvider';
const REFRESH_COUNTDOWN = 10; // seconds

/* eslint-disable max-statements */
const ClaimTokenModal = ({
  onClose,
  onSuccess,
  coinType
}) => {
  const dispatch = useTypedDispatch();
  const {
    confirmOpenWalletConnect
  } = useConfirmOpenWalletConnect();
  const coinInfo = coinTypeMap[coinType];
  const loading = useTypedSelector(state => state.claimToken.loading);
  const ingameBalance = useTypedSelector(state => state.ingameBalance.data);
  const profile = useTypedSelector(state => state.userProfile.user);
  const minClaimedFee = useTypedSelector(state => {
    var _state$ingameBalance$, _state$ingameBalance$2;

    switch (coinInfo.type) {
      case CurrencyType.GTHG:
        return (_state$ingameBalance$ = state.ingameBalance.data.config) === null || _state$ingameBalance$ === void 0 ? void 0 : _state$ingameBalance$.minFeeCashoutTHG;

      case CurrencyType.GTHC:
        return (_state$ingameBalance$2 = state.ingameBalance.data.config) === null || _state$ingameBalance$2 === void 0 ? void 0 : _state$ingameBalance$2.minFeeCashoutTHC;

      default:
        break;
    }

    return undefined;
  }) || 0;
  const claimedFeePercent = useTypedSelector(state => {
    var _state$ingameBalance$3, _state$ingameBalance$4;

    switch (coinInfo.type) {
      case CurrencyType.GTHG:
        return (_state$ingameBalance$3 = state.ingameBalance.data.config) === null || _state$ingameBalance$3 === void 0 ? void 0 : _state$ingameBalance$3.perFeeCashoutTHG;

      case CurrencyType.GTHC:
        return (_state$ingameBalance$4 = state.ingameBalance.data.config) === null || _state$ingameBalance$4 === void 0 ? void 0 : _state$ingameBalance$4.perFeeCashoutTHC;

      default:
        break;
    }

    return undefined;
  }) || 0;
  const thcSpentQuestionMask = useTypedSelector(state => {
    var _state$ingameBalance$5;

    return (_state$ingameBalance$5 = state.ingameBalance.data.config) === null || _state$ingameBalance$5 === void 0 ? void 0 : _state$ingameBalance$5.thcSpentQuestionMask;
  }) || 0;
  const timeToClaimToken = useTypedSelector(state => {
    var _state$ingameBalance$6;

    return (_state$ingameBalance$6 = state.ingameBalance.data.config) === null || _state$ingameBalance$6 === void 0 ? void 0 : _state$ingameBalance$6.timeToClaimToken;
  }) || 0;
  const thcBalance = useTypedSelector(state => state.connectWallet.thcBalance);
  const thgBalance = useTypedSelector(state => state.connectWallet.thgBalance);
  const userId = useTypedSelector(state => {
    var _state$userProfile$us;

    return (_state$userProfile$us = state.userProfile.user) === null || _state$userProfile$us === void 0 ? void 0 : _state$userProfile$us.id;
  });
  const cashOutLimit = useTypedSelector(state => state.cashOutLimit.data);
  const [price, setPrice] = React.useState(0);
  const [fee, setFee] = React.useState(0);
  const [error, setError] = React.useState(AppError.NO_ERROR);
  const [displayPrice, setDisplayPrice] = React.useState('');
  const currentBalance = getSystemCurrency(ingameBalance, coinType);
  const minConvertable = cashOutLimit ? formatCurrency(cashOutLimit.minCashOut, {
    precision: 0
  }) : undefined;
  const maxConvertable = cashOutLimit ? formatCurrency(cashOutLimit.maxCashOut, {
    precision: 0
  }) : undefined;
  const [exchangeRate, setExchangeRate] = React.useState(0);
  const [isDisabledInput, setIsDisabledInput] = React.useState(false);
  const [isDisabledConfirmed, setIsDisabledConfirmed] = React.useState(false);
  const [isShowRefreshButton, setIsShowRefreshButton] = React.useState(false);
  const [counter, setCounter] = React.useState(REFRESH_COUNTDOWN);
  const isClaimDisabled = !isDisabledConfirmed || loading || price < fee || !!error;
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (isClaimDisabled) {
        return;
      }

      if (counter === 0) {
        setError(AppError.QUOTE_EXPIRED);
        setCounter(REFRESH_COUNTDOWN);
        setIsShowRefreshButton(true);
      } else {
        setCounter(counter - 1);
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [counter, isClaimDisabled]);
  React.useEffect(() => {
    setFee(Math.max(claimedFeePercent * price, minClaimedFee));
  }, [price, minClaimedFee, claimedFeePercent]);

  const onClickConfirm = async () => {
    const systemPrice = normalNumberToSystemCurrency(price, coinInfo.type);
    const preparedData = await dispatch(prepareClaimToken({
      systemCurrency: systemPrice
    }));
    confirmOpenWalletConnect(async () => {
      const {
        data,
        error: claimError
      } = await dispatch(claimToken(preparedData));

      if (data) {
        onClose();
        onSuccess();

        if (thcBalance && thgBalance && userId) {
          logCoinChangeMarket({
            THC: thcBalance,
            THG: thgBalance,
            price: systemPrice,
            userId,
            source: 'MarketClaim',
            transactionId: data.id.toString()
          });
        }

        return;
      }

      if (claimError) {
        setError(claimError);
        return;
      }
    });
  };

  React.useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchIngameBalance());
      await dispatch(fetchCashOutLimit(coinInfo.currencyTo));
      const result = await dispatch(checkAccountCashout(coinInfo.type));

      if (result.error !== undefined && result.error !== AppError.NO_ERROR) {
        setError(result.error);
        setIsDisabledInput(true);
      }
    };

    fetchData();
  }, []);
  React.useEffect(() => {
    setError(checkOverPriceError(coinType, price, currentBalance));
  }, [price, currentBalance, coinType]);
  React.useEffect(() => {
    if (!cashOutLimit) {
      return;
    }

    const priceBN = new BN(normalNumberToSystemCurrency(price, coinInfo.type).value);
    const balanceBN = new BN(currentBalance.value);
    if (priceBN.gt(balanceBN)) switch (coinType) {
      case InGameCurrency.INGAME_THETAN_COIN:
        setError(AppError.NOT_ENOUGH_GTHC);
        return;

      case InGameCurrency.INGAME_THETAN_GEM:
        setError(AppError.NOT_ENOUGH_GTHG);
        return;

      default:
        setError(AppError.UNNKOWN);
        return;
    }
  }, [price, cashOutLimit, currentBalance, coinInfo.type, coinType]);

  const onClickRefresh = async () => {
    await dispatch(fetchIngameBalance());
    await checkCashoutPrice();
    setIsShowRefreshButton(false);
    setError(AppError.NO_ERROR);
  };

  const renderMessage = () => {
    switch (coinInfo.type) {
      case CurrencyType.GTHC:
        return /*#__PURE__*/React.createElement("ul", {
          className: styles.messageWrapper
        }, /*#__PURE__*/React.createElement("li", {
          className: styles.message
        }, "A ", claimedFeePercent * 100, "% fee of ", claimedFeePercent * price, "\xA0 THC will be translated and charged in THG"), /*#__PURE__*/React.createElement("li", {
          className: styles.fee
        }, "Fee chargable:\xA0", Math.max(minClaimedFee, claimedFeePercent * price * exchangeRate), "\xA0 THG, min fee: ", minClaimedFee, " THG"), /*#__PURE__*/React.createElement("li", {
          className: styles.rate
        }, "Rate:\xA0", /*#__PURE__*/React.createElement("span", {
          className: styles.gTHC
        }, exchangeRate !== 0 ? 1 : 0, "THC"), "\xA0=\xA0", /*#__PURE__*/React.createElement("span", {
          className: styles.gTHG
        }, exchangeRate, "THG")));

      case CurrencyType.GTHG:
        return /*#__PURE__*/React.createElement("div", {
          className: styles.messageWrapper
        }, /*#__PURE__*/React.createElement("li", {
          className: styles.message
        }, "A ", claimedFeePercent * 100, "% fee will be charged in THG"), /*#__PURE__*/React.createElement("li", {
          className: styles.fee
        }, "Fee chargable: ", fee, " THG, min fee: ", minClaimedFee, " THG"));

      default:
        return undefined;
    }
  };

  const renderPrimaryButton = () => {
    if (isShowRefreshButton) {
      return /*#__PURE__*/React.createElement("div", {
        className: styles.footer
      }, /*#__PURE__*/React.createElement(Button, {
        type: "warning",
        onClick: onClickRefresh,
        className: styles.btn,
        title: /*#__PURE__*/React.createElement("span", {
          className: styles.refreshBtn
        }, /*#__PURE__*/React.createElement("img", {
          className: styles.refreshIcon,
          src: icRefresh
        }), "Refresh")
      }));
    }

    return /*#__PURE__*/React.createElement("div", {
      className: styles.footer
    }, /*#__PURE__*/React.createElement(Button, {
      disabled: isClaimDisabled,
      onClick: onClickConfirm,
      className: styles.btn,
      type: "warning",
      title: loading ? 'Processing...' : isClaimDisabled ? 'Claim' : `Claim (${counter}s)`
    }));
  };

  const setMaxPrice = () => {
    if (!cashOutLimit) {
      return;
    }

    const maxClaimedBN = new BN(cashOutLimit.maxCashOut.value);
    const currentBalanceBN = new BN(currentBalance.value);
    let maxPrice;

    if (currentBalanceBN.gte(maxClaimedBN)) {
      maxPrice = formatCurrency(cashOutLimit.maxCashOut, {
        trimWholeFraction: true
      }).replace(',', '');
    } else {
      maxPrice = formatCurrency(currentBalance, {
        trimWholeFraction: true
      }).replace(',', '');
    }

    setPrice(Number(maxPrice));
    setDisplayPrice(maxPrice);
    setIsDisabledConfirmed(false);
    setIsShowRefreshButton(false);
  };

  const renderQuestionMarkContent = () => {
    const thcSpent = (cashOutLimit === null || cashOutLimit === void 0 ? void 0 : cashOutLimit.thcSpent) || 0;

    if (coinInfo.type === CurrencyType.GTHC && thcSpent > thcSpentQuestionMask) {
      return /*#__PURE__*/React.createElement("div", {
        className: styles.tooltip
      }, /*#__PURE__*/React.createElement("span", {
        className: styles.detail
      }, "The higher THC balance, the higher the conversion limits"));
    }

    return /*#__PURE__*/React.createElement("div", {
      className: styles.tooltip
    }, /*#__PURE__*/React.createElement("span", {
      className: styles.detail
    }, "Increase your purchase volume on Marketplace to increase conversion limits"));
  };

  const renderTooltip = position => {
    const renderContent = () => /*#__PURE__*/React.createElement(Tooltip, {
      color: '#27198D',
      content: renderQuestionMarkContent(),
      position: position
    }, /*#__PURE__*/React.createElement("img", {
      className: styles.questionMark,
      src: icQuestionMark
    }));

    return renderContent;
  };

  const checkCashoutPrice = async () => {
    const {
      error: confirmError,
      data
    } = await dispatch(confirmCashout(normalNumberToSystemCurrency(price, coinInfo.type)));
    setIsDisabledConfirmed(true);

    if (confirmError !== AppError.NO_ERROR) {
      setError(confirmError);
      return;
    }

    if (data) {
      setExchangeRate(data.exchangeRate);
    }

    setError(AppError.NO_ERROR);
    setCounter(REFRESH_COUNTDOWN);
  };

  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose,
    header: "Claim token"
  }, !loading ? /*#__PURE__*/React.createElement("span", {
    className: styles.desc
  }, "You are about to convert ", coinInfo.currencyFrom, " to\xA0", coinInfo.currencyTo, " token") : /*#__PURE__*/React.createElement("span", {
    className: styles.desc
  }, "Your ", coinInfo.nameTo, " will be claimed to your wallet. Please wait for a seconds...."), /*#__PURE__*/React.createElement("span", {
    className: classNames(styles.desc, styles.amount)
  }, "Available: ", /*#__PURE__*/React.createElement("span", null, formatCurrency(currentBalance))), /*#__PURE__*/React.createElement("div", {
    className: styles.inputView
  }, /*#__PURE__*/React.createElement("label", {
    className: styles.labelInput,
    htmlFor: "amount-converted"
  }, "Amount"), /*#__PURE__*/React.createElement("div", {
    className: styles.inputWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.priceRow
  }, /*#__PURE__*/React.createElement("input", {
    id: "amount-converted",
    placeholder: "Amount",
    className: classNames(styles.input, styles.price),
    type: "number",
    min: 0,
    disabled: isDisabledInput,
    value: displayPrice,
    onChange: e => {
      setDisplayPrice(e.target.value);
      const value = Number(e.target.value) || 0;
      setPrice(value);
      setIsDisabledConfirmed(false);
      setIsShowRefreshButton(false);
    }
  }), /*#__PURE__*/React.createElement("button", {
    disabled: isDisabledInput,
    onClick: setMaxPrice,
    className: styles.maxBtn
  }, "Max"), /*#__PURE__*/React.createElement("div", {
    className: styles.unit
  }, /*#__PURE__*/React.createElement("img", {
    draggable: false,
    src: coinInfo.image,
    className: styles.coin
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, coinInfo.currencyFrom))), /*#__PURE__*/React.createElement(Button, {
    title: "Confirm",
    onClick: checkCashoutPrice,
    disabled: isDisabledConfirmed || price <= 0,
    className: styles.confirmBtn
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.arrow
  }, /*#__PURE__*/React.createElement("img", {
    src: icArrowDown
  })), /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.priceRow, styles.priceEstimated)
  }, /*#__PURE__*/React.createElement("span", {
    className: classNames(styles.convertedPrice)
  }, ellipsisEnd(Math.max(price, 0).toString(), 17)), /*#__PURE__*/React.createElement("div", {
    className: styles.unit
  }, /*#__PURE__*/React.createElement("img", {
    draggable: false,
    src: coinInfo.image,
    className: styles.coin
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, coinInfo.currencyTo)))), /*#__PURE__*/React.createElement("div", {
    className: styles.warningWrapper
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.warning
  }, /*#__PURE__*/React.createElement("span", null, "Min convertable:\xA0"), /*#__PURE__*/React.createElement("span", null, minConvertable), /*#__PURE__*/React.createElement("span", null, "\xA0|\xA0Max convertable:\xA0"), /*#__PURE__*/React.createElement("span", null, maxConvertable)), coinInfo.type === CurrencyType.GTHC && /*#__PURE__*/React.createElement(BreakpointContent, {
    renderPhone: renderTooltip(TooltipPosition.LEFT),
    renderDesktop: renderTooltip(TooltipPosition.BOTTOM),
    renderTablet: renderTooltip(TooltipPosition.BOTTOM)
  })), renderMessage(), error !== AppError.NO_ERROR && /*#__PURE__*/React.createElement(ErrorView, {
    className: styles.error,
    title: formatErrorMessage(error, {
      cashOutDate: profile ? getNotOldErrorMessage(profile, timeToClaimToken) : '',
      minConvertable,
      maxConvertable,
      unit: coinInfo.currencyFrom
    }),
    type: error === AppError.QUOTE_EXPIRED ? 'warning' : 'error'
  }), renderPrimaryButton());
};

export default ClaimTokenModal;